/* Venty v2 PDP scripts - loaded once by sections/venty-v2-base.liquid (product.venty-v2 template only). */

/* ---------- Shared: smooth scrolling for in-page links ---------- */
/*
  "Compare with Pro" (#venty-v2-compare), the "Buy Now" jumps (#venty-v2-buy) and the hero star rating
  (#venty-v2-reviews) animate instead of jumping. Only acts on links inside venty-v2 sections and inside the
  main-product that follows the venty-v2 marker; honours prefers-reduced-motion and scroll-margin-top.
*/
(() => {
  const LINKS =
    '.venty-v2-section a[href^="#"], #MainContent > .venty-v2-base ~ [id$="__main-product"] a[href^="#venty-v2-"], #MainContent > .venty-v2-base ~ [id$="__main-product"] a.product__rating-link[href^="#"]';

  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target instanceof Element && event.target.closest(LINKS);
    if (!link) return;
    const id = decodeURIComponent(link.getAttribute('href').slice(1));
    const target = id && document.getElementById(id);
    if (!target) return;

    event.preventDefault();
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    if (history.pushState) history.pushState(null, '', '#' + id);
  });
})();

/* ---------- Hero (main-product) ---------- */
(() => {
  const HERO = '#MainContent > .venty-v2-base ~ [id$="__main-product"]';

  /* Mobile gallery counter ("1/4") over the main image. */
  const initCounter = () => {
    const gallery = document.querySelector(`${HERO} media-gallery`);
    if (!gallery || gallery.querySelector('.venty-v2-gallery-counter')) return;

    const slider = gallery.querySelector('slider-element');
    const dots = Array.from(gallery.querySelectorAll('.product__thumbnail'));
    if (!slider || dots.filter((dot) => !dot.hidden).length < 2) return;

    const counter = document.createElement('span');
    counter.className = 'venty-v2-gallery-counter';
    counter.setAttribute('aria-hidden', 'true');
    slider.parentElement.appendChild(counter);

    const update = () => {
      const visible = dots.filter((dot) => !dot.hidden);
      const current = visible.findIndex((dot) => dot.getAttribute('aria-current') === 'true');
      counter.textContent = `${Math.max(current, 0) + 1}/${visible.length}`;
    };

    update();
    new MutationObserver(update).observe(gallery.querySelector('.product__thumbnails-list') || gallery, {
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-current', 'hidden'],
    });
  };

  /* "Buy Now" / "Continue with Venty Original" target: the buy-button wrapper. */
  const ensureAnchor = () => {
    if (document.getElementById('venty-v2-buy')) return;
    const target = document.querySelector(`${HERO} product-info .product-form-wrapper`);
    if (target && !target.id) target.id = 'venty-v2-buy';
  };

  /*
    Text overrides from the Venty v2 base section: the colour picker label ("Choose Your Color:" -> "Color:") and the
    add-on's display name. Re-applied whenever the theme re-renders the product info (variant change).
  */
  const applyOverrides = () => {
    const config = document.querySelector('#MainContent > .venty-v2-base [data-venty-v2-overrides]');
    const info = document.querySelector(`${HERO} product-info`);
    if (!config || !info) return;

    // While "Buy 2" shows the second-fan picker, the main picker is fan 1.
    const twoFans = info.querySelector('[data-venty-v2-second-color]:not([hidden])');
    const colorLabel = twoFans ? 'Fan 1 color:' : config.dataset.colorLabel.trim();
    if (colorLabel) {
      info.querySelectorAll('variant-picker .form__label > div').forEach((label) => {
        const text = Array.from(label.childNodes).find((node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim());
        if (text && text.textContent.trim() !== colorLabel) text.textContent = `${colorLabel} `;
      });
    }

    const addonTitle = config.dataset.addonTitle.trim();
    if (addonTitle) {
      info.querySelectorAll('.product-bundle__title-label').forEach((title) => {
        if (title.textContent.trim() !== addonTitle) title.textContent = addonTitle;
      });
    }
  };

  let overridesObserver;
  const watchOverrides = () => {
    applyOverrides();
    const info = document.querySelector(`${HERO} product-info`);
    if (overridesObserver) overridesObserver.disconnect();
    if (!info) return;
    let queued = false;
    overridesObserver = new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        applyOverrides();
      });
    });
    overridesObserver.observe(info, { childList: true, subtree: true });
  };

  /*
    -/+ quantity box beside Add to cart, working together with the Buy 1 / Buy 2 cards.
    The theme hides its own quantity box whenever the cards exist (snippets/buy-buttons.liquid) and submits the
    cards' hidden quantity field, so this page adds its own box that drives that field: a card click updates the
    box; -/+ or typing sets the field and highlights the matching card (none for 3+, the field still carries it).
    The box's input has no name, so it is never submitted itself.
  */
  const addQuantityBox = () => {
    const breaks = document.querySelector(`${HERO} quantity-breaks-selector`);
    const breaksInput = breaks && breaks.querySelector('[data-quantity-breaks-input]');
    const buyButtons = document.querySelector(`${HERO} .buy-buttons`);
    if (!breaksInput || !buyButtons || buyButtons.querySelector('.venty-v2-qty')) return;

    const box = document.createElement('div');
    box.className = 'quantity venty-v2-qty inline-flex shrink-0 relative';
    box.innerHTML =
      '<button type="button" class="quantity__button" name="minus" aria-label="Decrease quantity"></button>' +
      '<input class="quantity__input text-center" type="number" inputmode="numeric" min="1" step="1" aria-label="Quantity">' +
      '<button type="button" class="quantity__button" name="plus" aria-label="Increase quantity"></button>';
    buyButtons.prepend(box);

    const input = box.querySelector('input');
    const minus = box.querySelector('[name="minus"]');

    const highlight = (quantity) => {
      breaks.querySelectorAll('[data-quantity-break]').forEach((card) => {
        const on = Number(card.dataset.quantityBreak) === quantity;
        card.classList.toggle('is-selected', on);
        card.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    };

    const show = (quantity) => {
      input.value = String(quantity);
      minus.disabled = quantity <= 1;
    };

    const set = (quantity) => {
      quantity = Math.max(1, Math.min(99, parseInt(quantity, 10) || 1));
      show(quantity);
      highlight(quantity);
      if (breaksInput.value === String(quantity)) return;
      breaksInput.value = String(quantity);
      breaksInput.dispatchEvent(new Event('change', { bubbles: true }));
    };

    minus.addEventListener('click', () => set((parseInt(input.value, 10) || 1) - 1));
    box.querySelector('[name="plus"]').addEventListener('click', () => set((parseInt(input.value, 10) || 1) + 1));
    input.addEventListener('change', () => set(input.value));
    breaksInput.addEventListener('change', () => show(Math.max(1, parseInt(breaksInput.value, 10) || 1)));

    show(Math.max(1, parseInt(breaksInput.value, 10) || 1));
  };

  /* The theme re-renders product info on variant change; re-add the box when it disappears. */
  const syncQuantity = () => {
    addQuantityBox();
    const info = document.querySelector(`${HERO} product-info`);
    if (!info || info.dataset.ventyV2QtyWatch) return;
    info.dataset.ventyV2QtyWatch = 'true';
    new MutationObserver(() => {
      if (!info.querySelector('.venty-v2-qty')) addQuantityBox();
    }).observe(info, { childList: true, subtree: true });
  };

  /*
    Buy 2 in two colours (snippets/venty-v2-second-color.liquid). While the quantity is exactly 2 the second-fan
    picker shows and the main picker's label reads "Fan 1 color:". With two different colours:
      - the Buy 2 card price and the Add to cart total are the sum of both fans (+ charger(s) when ticked),
      - Add to cart sends both variants (1 each) plus the charger, then refreshes the cart drawer like the theme does.
    With the same colour (or any other quantity) the theme's own add to cart runs unchanged.
  */
  const money = (cents) =>
    window.theme && theme.Currency
      ? theme.Currency.formatMoney(cents, theme.settings.currencyCodeEnabled ? theme.settings.moneyWithCurrencyFormat : theme.settings.moneyFormat)
      : `$${(cents / 100).toFixed(2)}`;

  const secondColor = { selectedId: null, overridden: false, quantityBeforeClick: null };

  const secondColorState = () => {
    const info = document.querySelector(`${HERO} product-info`);
    const picker = info && info.querySelector('[data-venty-v2-second-color]');
    const form = document.querySelector(`${HERO} form[data-type="add-to-cart-form"]`);
    const breaksInput = info && info.querySelector('quantity-breaks-selector [data-quantity-breaks-input]');
    if (!picker || !form || !breaksInput) return null;
    const mainId = form.querySelector('input[name="id"]') && form.querySelector('input[name="id"]').value;
    const swatches = Array.from(picker.querySelectorAll('[data-variant-id]'));
    const mainSwatch = swatches.find((swatch) => swatch.dataset.variantId === String(mainId));
    const secondSwatch = swatches.find((swatch) => swatch.dataset.variantId === String(secondColor.selectedId)) || mainSwatch;
    const charger = info.querySelector('.product-bundle__checkbox');
    return {
      info,
      picker,
      form,
      swatches,
      active: parseInt(breaksInput.value, 10) === 2,
      mainSwatch,
      secondSwatch,
      mixed: !!(mainSwatch && secondSwatch && mainSwatch !== secondSwatch),
      charger,
      chargerQuantity: charger && charger.closest('product-bundle-details')?.hasAttribute('data-match-main-quantity') ? 2 : 1,
    };
  };

  let pricing = false;
  const refreshSecondColor = () => {
    const state = secondColorState();
    if (!state || pricing) return;
    pricing = true;

    state.picker.hidden = !state.active;

    // Default the second fan to the first fan's colour until the shopper picks one.
    if (!secondColor.selectedId && state.mainSwatch) secondColor.selectedId = state.mainSwatch.dataset.variantId;
    const second = state.secondSwatch;
    state.swatches.forEach((swatch) => swatch.setAttribute('aria-checked', swatch === second ? 'true' : 'false'));
    const name = state.picker.querySelector('[data-second-color-name]');
    if (name && second && name.textContent !== second.dataset.name) name.textContent = second.dataset.name;
    const dot = state.picker.querySelector('[data-second-color-dot]');
    if (dot && second && dot.dataset.color !== second.dataset.color) dot.dataset.color = second.dataset.color;

    // Compare visible text only and write only when it differs: prices on this store get wrapped in
    // <span class="money"> after they are written, so comparing HTML would rewrite them forever.
    const setPrice = (node, cents) => {
      if (!node) return;
      const text = money(cents);
      const plain = (html) => {
        const div = document.createElement('div');
        div.innerHTML = html;
        return div.textContent.trim();
      };
      if (node.textContent.trim() !== plain(text)) node.innerHTML = text;
    };

    const breaks = state.info.querySelector('quantity-breaks-selector');
    const card = state.info.querySelector('[data-quantity-break-price="2"]');
    if (state.active && state.mixed) {
      // Two different colours: the card and the button show the sum of both fans (+ charger(s) when ticked).
      const fans = Number(state.mainSwatch.dataset.price) + Number(second.dataset.price);
      const chargerCents = state.charger && state.charger.checked ? Number(state.charger.dataset.price) * state.chargerQuantity : 0;
      setPrice(card, fans);
      state.info.querySelectorAll('[id^="BuyButtonPrice-"] product-buy-price').forEach((node) => setPrice(node, fans + chargerCents));
      secondColor.overridden = true;
    } else if (secondColor.overridden) {
      // Back to one colour (or another quantity): restore the theme's own numbers once.
      secondColor.overridden = false;
      if (state.mainSwatch) {
        const discount = parseInt((breaks && breaks.dataset.bundleDiscount) || '0', 10) || 0;
        setPrice(card, Number(state.mainSwatch.dataset.price) * 2 - discount);
      }
      state.info.querySelectorAll('[id^="BuyButtonPrice-"] product-buy-price').forEach((node) => {
        if (typeof node.updateTotal === 'function') node.updateTotal();
      });
    }
    pricing = false;
    applyOverrides();
  };

  /* At most one refresh per frame, so a back-and-forth with other scripts can never lock up the page. */
  let refreshQueued = false;
  const queueRefresh = () => {
    if (refreshQueued) return;
    refreshQueued = true;
    requestAnimationFrame(() => {
      refreshQueued = false;
      refreshSecondColor();
    });
  };

  /* Listeners sit on product-info and document (not on the picker or form), so they survive the theme
     re-rendering parts of the buy box on a colour change. */
  let secondColorBound = false;
  const initSecondColor = () => {
    const state = secondColorState();
    if (!state) return;
    refreshSecondColor();
    if (secondColorBound) return;
    secondColorBound = true;

    const dialog = () => state.info.querySelector('[data-second-color-dialog]');
    const openDialog = () => {
      const d = dialog();
      // Only worth asking when there is more than one colour to choose from.
      if (!d || d.open || d.querySelectorAll('[data-variant-id]:not(:disabled)').length < 2) return;
      d.showModal();
      const checked = d.querySelector('[aria-checked="true"]:not(:disabled)') || d.querySelector('[data-variant-id]:not(:disabled)');
      if (checked) checked.focus();
    };
    const closeDialog = () => {
      const d = dialog();
      if (d && d.open) d.close();
    };

    // Capture phase: note the quantity before the cards' own click handler changes it.
    state.info.addEventListener(
      'click',
      () => {
        const input = state.info.querySelector('quantity-breaks-selector [data-quantity-breaks-input]');
        secondColor.quantityBeforeClick = input ? input.value : null;
      },
      true
    );

    state.info.addEventListener('click', (event) => {
      const option = event.target.closest('[data-second-color-dialog] [data-variant-id]');
      if (option && !option.disabled) {
        secondColor.selectedId = option.dataset.variantId;
        setTimeout(closeDialog, 160);
      }
      if (event.target.closest('[data-second-color-open]')) openDialog();
      if (event.target.closest('[data-second-color-close]') || event.target.matches('[data-second-color-dialog]')) closeDialog();

      // Picking "Buy 2" (coming from another quantity) asks for the second colour right away.
      const card = event.target.closest('.quantity-breaks__option[data-quantity-break="2"]');
      if (card && secondColor.quantityBeforeClick !== '2') setTimeout(openDialog, 60);

      // Re-check after anything that changes quantity, colour or charger.
      queueRefresh();
    });
    state.info.addEventListener('change', queueRefresh);
    // ...and after the theme rewrites its own price text.
    new MutationObserver(queueRefresh).observe(state.info, { childList: true, subtree: true, characterData: true });

    // Add both colours to the cart (capture phase on document runs before the theme's own submit handler).
    document.addEventListener(
      'submit',
      (event) => {
        const now = secondColorState();
        if (!now || event.target !== now.form || !now.active || !now.mixed) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        addTwoColors(now);
      },
      true
    );
  };

  const addTwoColors = async (state) => {
    const button = state.form.querySelector('[type="submit"]');
    if (button && button.getAttribute('aria-busy') === 'true') return;
    if (button) button.setAttribute('aria-busy', 'true');

    const items = [
      { id: Number(state.mainSwatch.dataset.variantId), quantity: 1 },
      { id: Number(state.secondSwatch.dataset.variantId), quantity: 1 },
    ];
    if (state.charger && state.charger.checked) items.push({ id: Number(state.charger.value), quantity: state.chargerQuantity });

    try {
      const root = (window.theme && theme.routes && theme.routes.root_url) || '/';
      const response = await fetch(`${root.replace(/\/$/, '')}/cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const result = await response.json();
      const cart = await (await fetch(`${root.replace(/\/$/, '')}/cart.js`, { headers: { Accept: 'application/json' } })).json();

      if (window.theme && theme.pubsub) {
        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', cart });
      }
      if (result.status) {
        const error = state.form.querySelector('.product-form__error-message');
        if (error) {
          error.textContent = result.description || result.message || '';
          error.hidden = false;
        }
        return;
      }

      const drawer = document.querySelector('cart-drawer');
      if (drawer && !(window.theme && theme.settings && theme.settings.cartType === 'page')) {
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } }));
      } else {
        window.location.href = (window.theme && theme.routes && theme.routes.cart_url) || '/cart';
      }
    } catch (error) {
      console.error(error);
    } finally {
      if (button) button.removeAttribute('aria-busy');
    }
  };


  const init = () => {
    initCounter();
    ensureAnchor();
    watchOverrides();
    syncQuantity();
    initSecondColor();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();

/* ---------- <venty-v2-carousel> ---------- */
/*
  Horizontal scroll-snap slider. Markup: [data-track] holding [data-slide] items, optional [data-prev] / [data-next]
  buttons and a [data-dots] container (dots are generated, one per scroll position).
    data-mobile-only  - acts below 1024px only (the desktop layout is a static grid in CSS)
    data-center       - slides snap to the centre; starts on the second slide when there are 3 or more
    data-loop         - arrows wrap around at either end
*/
(() => {
  const NAME = 'venty-v2-carousel';
  if (customElements.get(NAME)) return;
  const MOBILE = window.matchMedia('(max-width: 1023.98px)');

  class VentyV2Carousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;
      this.slides = Array.from(this.track.querySelectorAll('[data-slide]'));
      this.dotsEl = this.querySelector('[data-dots]');
      this.prevBtn = this.querySelector('[data-prev]');
      this.nextBtn = this.querySelector('[data-next]');
      this.center = this.hasAttribute('data-center');
      this.loop = this.hasAttribute('data-loop');
      this.mobileOnly = this.hasAttribute('data-mobile-only');

      this.onScroll = this.onScroll.bind(this);
      this.refresh = this.refresh.bind(this);

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.go(this.index - 1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.go(this.index + 1));
      if (this.dotsEl) {
        this.dotsEl.addEventListener('click', (event) => {
          const dot = event.target.closest('[data-dot]');
          if (dot) this.go(Number(dot.dataset.dot));
        });
      }

      this.resizeObserver = new ResizeObserver(this.refresh);
      this.resizeObserver.observe(this.track);
      MOBILE.addEventListener('change', this.refresh);

      this.index = 0;
      this.refresh();
      if (this.center && this.slides.length >= 3 && this.isActive()) {
        requestAnimationFrame(() => this.go(1, 'auto'));
      }
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
      MOBILE.removeEventListener('change', this.refresh);
    }

    isActive() {
      return !this.mobileOnly || MOBILE.matches;
    }

    /* Scroll positions the track can actually reach, one per slide (duplicates at the end collapse). */
    positions() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const trackLeft = this.track.getBoundingClientRect().left - this.track.scrollLeft;
      const padStart = parseFloat(getComputedStyle(this.track).scrollPaddingInlineStart) || 0;
      const list = [];
      // Slides whose snap point is switched off in CSS (e.g. two logos per page) are not stops.
      const stops = this.slides.filter((slide) => getComputedStyle(slide).scrollSnapAlign !== 'none');
      (stops.length ? stops : this.slides).forEach((slide) => {
        const rect = slide.getBoundingClientRect();
        let pos = this.center
          ? rect.left - trackLeft - (this.track.clientWidth - rect.width) / 2
          : rect.left - trackLeft - padStart;
        pos = Math.max(0, Math.min(max, Math.round(pos)));
        if (!list.length || Math.abs(pos - list[list.length - 1]) > 2) list.push(pos);
      });
      return list.length ? list : [0];
    }

    refresh() {
      this.classList.toggle('is-static', !this.isActive());
      this.pos = this.positions();
      this.classList.toggle('is-single', this.pos.length < 2);
      this.renderDots();
      this.onScroll();
    }

    renderDots() {
      if (!this.dotsEl) return;
      const count = this.isActive() ? this.pos.length : 0;
      if (this.dotsEl.childElementCount === count) return;
      this.dotsEl.innerHTML = '';
      for (let i = 0; i < count && count > 1; i++) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'venty-v2-dots__dot';
        dot.dataset.dot = i;
        dot.tabIndex = -1;
        this.dotsEl.appendChild(dot);
      }
    }

    onScroll() {
      if (!this.pos) return;
      const left = this.track.scrollLeft;
      let best = 0;
      this.pos.forEach((pos, i) => {
        if (Math.abs(pos - left) < Math.abs(this.pos[best] - left)) best = i;
      });
      this.index = best;
      if (this.dotsEl) {
        Array.from(this.dotsEl.children).forEach((dot, i) => dot.classList.toggle('is-active', i === best));
      }
      if (this.center) {
        const centre = left + this.track.clientWidth / 2;
        const trackLeft = this.track.getBoundingClientRect().left - this.track.scrollLeft;
        this.slides.forEach((slide) => {
          const rect = slide.getBoundingClientRect();
          const slideCentre = rect.left - trackLeft + rect.width / 2;
          slide.classList.toggle('is-active', Math.abs(slideCentre - centre) < rect.width / 2);
        });
      }
      const last = this.pos.length - 1;
      if (this.prevBtn) this.prevBtn.disabled = !this.loop && best <= 0;
      if (this.nextBtn) this.nextBtn.disabled = !this.loop && best >= last;
    }

    go(i, behavior) {
      if (!this.pos || !this.pos.length) return;
      const last = this.pos.length - 1;
      if (this.loop) {
        i = i < 0 ? last : i > last ? 0 : i;
      } else {
        i = Math.max(0, Math.min(last, i));
      }
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollTo({ left: this.pos[i], behavior: behavior || (reduceMotion ? 'auto' : 'smooth') });
    }
  }

  customElements.define(NAME, VentyV2Carousel);
})();

/* ---------- <venty-v2-tabs> ---------- */
/* Tab buttons ([role=tab]) switch the [role=tabpanel] named by aria-controls. Arrow keys move between tabs. */
(() => {
  const NAME = 'venty-v2-tabs';
  if (customElements.get(NAME)) return;

  class VentyV2Tabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      if (!this.tabs.length) return;

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.select(tab));
        tab.addEventListener('keydown', (event) => {
          const i = this.tabs.indexOf(tab);
          let next = null;
          if (event.key === 'ArrowRight') next = this.tabs[(i + 1) % this.tabs.length];
          if (event.key === 'ArrowLeft') next = this.tabs[(i - 1 + this.tabs.length) % this.tabs.length];
          if (event.key === 'Home') next = this.tabs[0];
          if (event.key === 'End') next = this.tabs[this.tabs.length - 1];
          if (!next) return;
          event.preventDefault();
          this.select(next);
          next.focus();
        });
      });

      // The "open on load" tab may have been hidden (no photos): fall back to the first visible one.
      const selected = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true');
      this.select(selected || this.tabs[0]);
    }

    select(active) {
      this.tabs.forEach((tab) => {
        const on = tab === active;
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.tabIndex = on ? 0 : -1;
        const panel = document.getElementById(tab.getAttribute('aria-controls'));
        if (panel) panel.hidden = !on;
      });
    }
  }

  customElements.define(NAME, VentyV2Tabs);
})();

/* ---------- <venty-v2-video> ---------- */
/*
  Play / pause toggle ([data-toggle]) for the <video> elements inside. With data-autoplay the visible video plays
  muted while at least a quarter of it is on screen (and pauses when scrolled away); otherwise it starts on the
  first press (with sound) and pauses any other Venty v2 video that is playing.
  Adds .is-playing while playing and .is-started once a video has played (CSS then hides the cover image).
*/
(() => {
  const NAME = 'venty-v2-video';
  if (customElements.get(NAME)) return;

  class VentyV2Video extends HTMLElement {
    connectedCallback() {
      this.videos = Array.from(this.querySelectorAll('video'));
      if (!this.videos.length) return;

      this.button = this.querySelector('[data-toggle]');
      this.autoplay = this.hasAttribute('data-autoplay') && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.wantPlay = this.autoplay;
      this.inView = false;
      this.update = this.update.bind(this);
      this.sync = this.sync.bind(this);

      this.videos.forEach((video) => {
        video.addEventListener('play', this.update);
        video.addEventListener('pause', this.update);
        video.addEventListener('playing', () => this.classList.add('is-started'));
      });

      const toggle = () => {
        this.wantPlay = !this.isPlaying();
        if (this.wantPlay) {
          document.dispatchEvent(new CustomEvent('venty-v2:play', { detail: this }));
          if (!this.autoplay) this.videos.forEach((video) => (video.muted = false));
        }
        this.sync();
      };
      if (this.button) this.button.addEventListener('click', toggle);
      // Cards without autoplay: a tap anywhere on the card toggles too.
      if (!this.autoplay) {
        this.addEventListener('click', (event) => {
          if (this.button && this.button.contains(event.target)) return;
          toggle();
        });
      }

      this.onOtherPlay = (event) => {
        if (event.detail !== this && !this.autoplay && this.isPlaying()) {
          this.wantPlay = false;
          this.sync();
        }
      };
      document.addEventListener('venty-v2:play', this.onOtherPlay);

      this.observer = new IntersectionObserver(
        (entries) => {
          this.inView = entries[entries.length - 1].isIntersecting;
          if (!this.inView && !this.autoplay) this.wantPlay = false;
          this.sync();
        },
        { threshold: 0.25 }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      document.removeEventListener('venty-v2:play', this.onOtherPlay);
    }

    currentVideo() {
      return this.videos.find((video) => video.getClientRects().length) || null;
    }

    isPlaying() {
      return this.videos.some((video) => !video.paused);
    }

    sync() {
      const current = this.currentVideo();
      this.videos.forEach((video) => {
        if (video === current && this.wantPlay && this.inView) {
          const attempt = video.play();
          if (attempt && attempt.catch) {
            attempt.catch(() => {
              // Sound refused without a fresh tap (iOS): keep playing muted.
              if (!video.muted) {
                video.muted = true;
                video.play().catch(() => {});
              }
            });
          }
        } else if (!video.paused) {
          video.pause();
        }
      });
      this.update();
    }

    update() {
      const playing = this.isPlaying();
      this.classList.toggle('is-playing', playing);
      if (this.button) this.button.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
    }
  }

  customElements.define(NAME, VentyV2Video);
})();
